import { bookableFactory } from "@@/shared/acquire/bookable";
import { contentBlockFactory } from "@@/shared/acquire/content-block";
import { resourceReferenceFromTemplateFactory } from "@@/shared/acquire/resource-references/resource-reference-template";
import { voucherFactory } from "@@/shared/acquire/voucher";
import type { IsoAndUnixTimestamp } from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type {
    DurationInDays,
    DurationInMinutes,
} from "@@/shared/date-time/time-spans";
import { inHours } from "@@/shared/date-time/time-spans";
import type { DiscountId } from "@@/shared/discounts/discount-id";
import { discountIdZodSchema } from "@@/shared/discounts/discount-id";
import type { OptionalGroupId } from "@@/shared/optionals/optional-group-id";
import { priceZodSchema, zeroPrice } from "@@/shared/prices/price";
import type { ProductTemplate } from "@@/shared/products/templates/product-template";
import type { AlfredItemStatus } from "@@/shared/sections/alfred-item-status";
import {
    alfredItemStatusZodSchema,
    defaultAlfredItemStatus,
} from "@@/shared/sections/alfred-item-status";
import { amountFactory } from "@@/shared/units-and-quantities/amount";
import { units } from "@@/shared/units-and-quantities/unit";
import { assertTypeMatch } from "@@/utilities/type-helpers";
import type { Except, IsEqual } from "type-fest";
import { z } from "zod";
import type { AcquireType } from "../acquire/acquirable";

import { buyableFactory } from "@@/shared/acquire/buyable";
import { giftCardPriceSettingsZodObject } from "../acquire/gift-card-price-settings";
import { assertNever } from "../assert-never";
import { weekAvailabilityZodSchema } from "../date-time/availability/week-availability";
import { entityStateZodSchema } from "../entity-status";
import {
    metaContentFactory,
    metaContentZodSchema,
} from "../meta-content/meta-content";
import { optionalGroupIdZodSchema } from "../optionals/optional-group-id";
import { findMinPrice } from "../prices/find-min-price";
import type { ProviderId } from "../providers/provider-id";
import { providerIdZodSchema } from "../providers/provider-id";
import { replacePlaceholderInTranslatable } from "../translations/text-utils";
import type { ProductAddCommand } from "./commands/product-add-command";
import type { ProductId } from "./product-id";
import { productIdFactory, productIdZodSchema } from "./product-id";
import {
    DefaultProductNotification,
    productNotificationZodSchema,
} from "./product-notification";
import type { Sku } from "./sku";
import {
    accommodationSkuZodObject,
    bookableSkuFactory,
    bookableSkuZodObject,
    buyableSkuFactory,
    buyableSkuZodObject,
    giftCardSkuZodObject,
    skuFactory,
    voucherSkuFactory,
    voucherSkuZodObject,
} from "./sku";
import { calculateMinPrice } from "./sku-pricing";

const productScope = "products";

const _productBaseZodObject = z.object({
    _id: productIdZodSchema,
    _type: z.literal("PRODUCT"),
    _createdAt: isoAndUnixZodSchema,
    _status: entityStateZodSchema,
    discountIds: z.array(discountIdZodSchema),
    meta: metaContentZodSchema,
    /**
     * If there's a min level of items of this type to buy/book at the samte time.
     * E.g. You can only order a dish for at least two people, it's not made for only one person.
     * @type {number}
     */
    minAcquireQuantity: z.number().nullish(),
    price: priceZodSchema,
    providerId: providerIdZodSchema,
    status: alfredItemStatusZodSchema,
    shortLink: z.string().optional(),
    optionalGroupIds: z.array(optionalGroupIdZodSchema),
    availability: weekAvailabilityZodSchema.optional(),
    notificationSettings: productNotificationZodSchema.optional(),
    duplicatedFrom: productIdZodSchema.optional(),
    handleStatusPerOrderedItem: z.boolean().optional(),
});

const giftCardProductZodObject = _productBaseZodObject.merge(
    z.object({
        acquirableAs: z.literal("GIFTCARD"),
        priceSettings: giftCardPriceSettingsZodObject.optional(),
        skus: z.array(giftCardSkuZodObject),
    }),
);
type GiftCard_Product = z.infer<typeof giftCardProductZodObject>;
const buyableProductZodObject = _productBaseZodObject.merge(
    z.object({
        acquirableAs: z.literal("BUYABLE"),
        skus: z.array(buyableSkuZodObject),
    }),
);
type Buyable_Product = z.infer<typeof buyableProductZodObject>;
const accommodationProductZodObject = _productBaseZodObject.merge(
    z.object({
        acquirableAs: z.literal("ACCOMMODATION"),
        skus: z.array(accommodationSkuZodObject),
    }),
);
type Accommodation_Product = z.infer<typeof accommodationProductZodObject>;
const bookableProductZodObject = _productBaseZodObject.merge(
    z.object({
        acquirableAs: z.literal("BOOKABLE"),
        skus: z.array(bookableSkuZodObject),
    }),
);
type Bookable_Product = z.infer<typeof bookableProductZodObject>;
const voucherProductZodObject = _productBaseZodObject.merge(
    z.object({
        acquirableAs: z.literal("VOUCHER"),
        skus: z.array(voucherSkuZodObject),
    }),
);
type Voucher_Product = z.infer<typeof voucherProductZodObject>;

const productZodObject = z.discriminatedUnion("acquirableAs", [
    giftCardProductZodObject,
    accommodationProductZodObject,
    buyableProductZodObject,
    bookableProductZodObject,
    voucherProductZodObject,
]);

type Product = z.infer<typeof productZodObject>;

type ProductTypeMap = {
    GIFTCARD: GiftCard_Product;
    ACCOMMODATION: Accommodation_Product;
    BUYABLE: Buyable_Product;
    BOOKABLE: Bookable_Product;
    VOUCHER: Voucher_Product;
};
const giftCardProductFactoryParamsZodObject = giftCardProductZodObject.omit({
    _id: true,
    _type: true,
    _createdAt: true,
    _status: true,
    status: true,
    price: true,
    optionalGroupIds: true,
    discountIds: true,
});
type GiftCardProductFactoryParams = z.infer<
    typeof giftCardProductFactoryParamsZodObject
>;
const accommodationProductFactoryParamsZodObject =
    accommodationProductZodObject.omit({
        _id: true,
        _type: true,
        _createdAt: true,
        _status: true,
        status: true,
        price: true,
        optionalGroupIds: true,
        discountIds: true,
    });
type AccommodationProductFactoryParams = z.infer<
    typeof accommodationProductFactoryParamsZodObject
>;
const buyableProductFactoryParamsZodObject = buyableProductZodObject.omit({
    _id: true,
    _type: true,
    _createdAt: true,
    _status: true,
    status: true,
    price: true,
    optionalGroupIds: true,
    discountIds: true,
});
type BuyableProductFactoryParams = z.infer<
    typeof buyableProductFactoryParamsZodObject
>;
const bookableProductFactoryParamsZodObject = bookableProductZodObject.omit({
    _id: true,
    _type: true,
    _createdAt: true,
    _status: true,
    status: true,
    price: true,
    optionalGroupIds: true,
    discountIds: true,
});
type BookableProductFactoryParams = z.infer<
    typeof bookableProductFactoryParamsZodObject
>;
const voucherProductFactoryParamsZodObject = voucherProductZodObject.omit({
    _id: true,
    _type: true,
    _createdAt: true,
    _status: true,
    status: true,
    price: true,
    optionalGroupIds: true,
    discountIds: true,
});
type VoucherProductFactoryParams = z.infer<
    typeof voucherProductFactoryParamsZodObject
>;

const _factoryParamsZodUnion = z.union([
    giftCardProductFactoryParamsZodObject,
    accommodationProductFactoryParamsZodObject,
    buyableProductFactoryParamsZodObject,
    bookableProductFactoryParamsZodObject,
    voucherProductFactoryParamsZodObject,
]);

type FactoryParams = z.infer<typeof _factoryParamsZodUnion>;
type FactoryParamsTypeMap = {
    GIFTCARD: GiftCardProductFactoryParams;
    ACCOMMODATION: AccommodationProductFactoryParams;
    BUYABLE: BuyableProductFactoryParams;
    BOOKABLE: BookableProductFactoryParams;
    VOUCHER: VoucherProductFactoryParams;
};

// make sure we've got all type types of `AcquireType`
// covered as keys in the `FactoryParamsTypeMap`
assertTypeMatch<IsEqual<keyof FactoryParamsTypeMap, AcquireType>>();

const productFactory = {
    from: (
        params: FactoryParams & {
            _id?: ProductId;
            optionalGroupIds?: OptionalGroupId[];
            _createdAt?: IsoAndUnixTimestamp;
            discountIds?: DiscountId[];
            status?: AlfredItemStatus;
        },
    ): Product => {
        const productPrice = findMinPrice(
            params.skus,
            (sku: Sku) => calculateMinPrice(sku.acquire, []),
            true,
        );
        if (!productPrice) {
            // eslint-disable-next-line no-console
            console.error(
                "Could not set price on product based on sku prices",
                productPrice,
                JSON.stringify(params.skus.map(s => s.acquire.price)),
            );
            throw new Error(
                "Could not set price on product based on sku prices",
            );
        }
        const productId = params._id || productIdFactory();
        switch (params.acquirableAs) {
            case "GIFTCARD": {
                const result: GiftCard_Product = {
                    ...params,
                    _id: productId,
                    _status: "ACTIVE",
                    _type: "PRODUCT",
                    _createdAt: params._createdAt || nowIsoAndUnix(),
                    optionalGroupIds: params.optionalGroupIds || [],
                    discountIds: params.discountIds ?? [],
                    price: productPrice,
                    minAcquireQuantity: params.minAcquireQuantity || null,
                    status: params.status || defaultAlfredItemStatus,
                    availability: params.availability,
                    notificationSettings: params.notificationSettings,
                    duplicatedFrom: params.duplicatedFrom,
                    skus: params.skus,
                    priceSettings: params.priceSettings,
                };
                return result;
            }
            case "ACCOMMODATION":
            case "BUYABLE":
            case "BOOKABLE":
            case "VOUCHER":
                return {
                    ...params,
                    _id: params._id || productIdFactory(),
                    _status: "ACTIVE",
                    _type: "PRODUCT",
                    _createdAt: params._createdAt || nowIsoAndUnix(),
                    optionalGroupIds: params.optionalGroupIds || [],
                    discountIds: params.discountIds ?? [],
                    price: productPrice,
                    minAcquireQuantity: params.minAcquireQuantity || null,
                    status: params.status || defaultAlfredItemStatus,
                    availability: params.availability,
                    notificationSettings: params.notificationSettings,
                    duplicatedFrom: params.duplicatedFrom,
                };
            default:
                assertNever(params);
        }
    },
    fromTemplate: (params: {
        productTemplate: ProductTemplate;
        forProviderId: ProviderId;
    }): Product => {
        const { productTemplate, forProviderId } = params;
        if (
            !["BOOKABLE", "BUYABLE", "VOUCHER"].includes(productTemplate.type)
        ) {
            throw new Error(
                `Unhandled product template type; ${productTemplate.type}`,
            );
        }

        const productId = productIdFactory();
        if (productTemplate.type === "BOOKABLE") {
            const bookableSku = bookableSkuFactory({
                productId,
                acquire: bookableFactory({
                    price: zeroPrice,
                    // TODO: Amount read from template?
                    amount: amountFactory({
                        unit: units.pcs,
                        value: 1,
                        quantity: 1,
                    }),
                    resources: [],
                    extras: [],
                    extrasGroups: [],
                    sessions: undefined,
                    contentBlocks: productTemplate.contentBlocks.map(cb =>
                        contentBlockFactory({
                            title: cb.title,
                            content: cb.content,
                        }),
                    ),
                    main: resourceReferenceFromTemplateFactory(productTemplate),
                    settings: {
                        postTime: 0 as DurationInMinutes,
                        prepTime: 0 as DurationInMinutes,
                        allowBookingDuringSession: false,
                        orderConfirm: "AUTO",
                    },
                    // TODO: Read from template?
                    type: "GENERIC",
                    hasAccess: false,
                }),
            });
            return productFactory.from({
                _id: productId,
                meta: { ...productTemplate.meta },
                providerId: forProviderId,
                acquirableAs: productTemplate.type,
                skus: [bookableSku],
                notificationSettings: DefaultProductNotification,
            });
        }

        if (productTemplate.type === "VOUCHER") {
            const voucherSku = voucherSkuFactory({
                productId,
                acquire: voucherFactory({
                    price: zeroPrice,
                    // TODO: Amount read from template?
                    amount: amountFactory({
                        unit: units.pcs,
                        value: 1,
                        quantity: 1,
                    }),

                    valid: inHours({ days: 365 as DurationInDays }),
                    contentBlocks: productTemplate.contentBlocks.map(cb =>
                        contentBlockFactory({
                            title: cb.title,
                            content: cb.content,
                        }),
                    ),
                }),
            });

            return productFactory.from({
                _id: productId,
                meta: { ...productTemplate.meta },
                providerId: forProviderId,
                acquirableAs: productTemplate.type,
                skus: [voucherSku],
            });
        }

        if (productTemplate.type === "BUYABLE") {
            const sku = buyableSkuFactory({
                productId,
                trackStockBalance: false,
                acquire: buyableFactory({
                    price:
                        productTemplate.templateDescription?.suggestedPriceRange
                            ?.min ?? zeroPrice,
                }),
            });
            return productFactory.from({
                _id: productId,
                meta: { ...productTemplate.meta },
                providerId: forProviderId,
                acquirableAs: productTemplate.type,
                skus: [sku],
            });
        }

        throw new Error("Unhandled product template type");
    },
    fromProductAddCommand: <AcquirableAs extends AcquireType>(
        command: Except<ProductAddCommand, "acquirableAs"> & {
            acquirableAs: AcquirableAs;
        },
    ): Product => {
        switch (command.acquirableAs) {
            case "GIFTCARD": {
                const sku =
                    skuFactory.fromProductAddCommand<"GIFTCARD">(command);
                const product: GiftCard_Product = {
                    _id: command.productId,
                    _type: "PRODUCT",
                    _createdAt: nowIsoAndUnix(),
                    _status: "ACTIVE",
                    status: defaultAlfredItemStatus,
                    providerId: command.providerId,
                    acquirableAs: command.acquirableAs,
                    meta: metaContentFactory({
                        title: command.title,
                        tags: command.tags,
                        subtitle: command.subtitle,
                        kicker: command.kicker
                            ? replacePlaceholderInTranslatable(
                                  command.kicker,
                                  "*",
                                  "•",
                              )
                            : undefined,
                        description: command.description,
                    }),
                    availability: command.availability,
                    discountIds: command.discountIds ?? [],
                    skus: [sku],
                    optionalGroupIds: command.optionalGroupIds || [],
                    price: command.price,
                };
                return product;
            }
            case "ACCOMMODATION": {
                const sku =
                    skuFactory.fromProductAddCommand<"ACCOMMODATION">(command);
                const product: Accommodation_Product = {
                    _id: command.productId,
                    _type: "PRODUCT",
                    _createdAt: nowIsoAndUnix(),
                    _status: "ACTIVE",
                    status: defaultAlfredItemStatus,
                    providerId: command.providerId,
                    acquirableAs: "ACCOMMODATION",
                    meta: metaContentFactory({
                        title: command.title,
                        tags: command.tags,
                        subtitle: command.subtitle,
                        kicker: command.kicker
                            ? replacePlaceholderInTranslatable(
                                  command.kicker,
                                  "*",
                                  "•",
                              )
                            : undefined,
                        description: command.description,
                    }),
                    availability: command.availability,
                    discountIds: command.discountIds ?? [],
                    skus: [sku],
                    optionalGroupIds: command.optionalGroupIds || [],
                    price: command.price,
                };
                return product;
            }
            case "BUYABLE": {
                const sku =
                    skuFactory.fromProductAddCommand<"BUYABLE">(command);
                const product: Buyable_Product = {
                    _id: command.productId,
                    _type: "PRODUCT",
                    _createdAt: nowIsoAndUnix(),
                    _status: "ACTIVE",
                    status: defaultAlfredItemStatus,
                    providerId: command.providerId,
                    acquirableAs: "BUYABLE",
                    meta: metaContentFactory({
                        title: command.title,
                        tags: command.tags,
                        subtitle: command.subtitle,
                        kicker: command.kicker
                            ? replacePlaceholderInTranslatable(
                                  command.kicker,
                                  "*",
                                  "•",
                              )
                            : undefined,
                        description: command.description,
                    }),
                    availability: command.availability,
                    discountIds: command.discountIds ?? [],
                    skus: [sku],
                    optionalGroupIds: command.optionalGroupIds || [],
                    price: command.price,
                };
                return product;
            }
            case "BOOKABLE": {
                const sku =
                    skuFactory.fromProductAddCommand<"BOOKABLE">(command);
                const product: Bookable_Product = {
                    _id: command.productId,
                    _type: "PRODUCT",
                    _createdAt: nowIsoAndUnix(),
                    _status: "ACTIVE",
                    status: defaultAlfredItemStatus,
                    providerId: command.providerId,
                    acquirableAs: "BOOKABLE",
                    meta: metaContentFactory({
                        title: command.title,
                        tags: command.tags,
                        subtitle: command.subtitle,
                        kicker: command.kicker
                            ? replacePlaceholderInTranslatable(
                                  command.kicker,
                                  "*",
                                  "•",
                              )
                            : undefined,
                        description: command.description,
                    }),
                    availability: command.availability,
                    discountIds: command.discountIds ?? [],
                    skus: [sku],
                    optionalGroupIds: command.optionalGroupIds || [],
                    price: command.price,
                };
                return product;
            }
            case "VOUCHER": {
                const sku =
                    skuFactory.fromProductAddCommand<"VOUCHER">(command);
                const product: Voucher_Product = {
                    _id: command.productId,
                    _type: "PRODUCT",
                    _createdAt: nowIsoAndUnix(),
                    _status: "ACTIVE",
                    status: defaultAlfredItemStatus,
                    providerId: command.providerId,
                    acquirableAs: "VOUCHER",
                    meta: metaContentFactory({
                        title: command.title,
                        tags: command.tags,
                        subtitle: command.subtitle,
                        kicker: command.kicker
                            ? replacePlaceholderInTranslatable(
                                  command.kicker,
                                  "*",
                                  "•",
                              )
                            : undefined,
                        description: command.description,
                    }),
                    availability: command.availability,
                    discountIds: command.discountIds ?? [],
                    skus: [sku],
                    optionalGroupIds: command.optionalGroupIds || [],
                    price: command.price,
                };
                return product;
            }

            default:
                assertNever(command.acquirableAs);
        }
    },
};

type ProductReference = {
    id: ProductId;
    type: "PRODUCT";
};

// const productZodSchema: z.ZodSchema<Product> = z.object({
//     _id: productIdZodSchema,
//     _type: z.literal("PRODUCT"),
//     _createdAt: isoAndUnixZodSchema,
//     _status: entityStateZodSchema,
//     acquirableAs: acquireTypeZodSchema,
//     discountIds: z.array(discountIdZodSchema),
//     meta: metaContentZodSchema,
//     minAcquireQuantity: z.number().nullish(),
//     price: priceZodSchema,
//     providerId: providerIdZodSchema,
//     status: alfredItemStatusZodSchema,
//     shortLink: z.string().optional(),
//     skus: z.array(skuZodSchema),
//     optionalGroupIds: z.array(optionalGroupIdZodSchema),
//     availability: weekAvailabilityZodSchema.optional(),
//     notificationSettings: productNotificationZodSchema,
//     duplicatedFrom: productIdZodSchema.optional(),
//     handleStatusPerOrderedItem: z.boolean().optional(),
// });

export { productFactory, productScope, productZodObject };
export type { Product, ProductReference };

export type {
    Accommodation_Product,
    Bookable_Product,
    Buyable_Product,
    GiftCard_Product,
    ProductTypeMap,
    Voucher_Product,
};
